/* Phase 5+ · Piece Lifecycle timeline card
 * Vertical event stream aggregating GRN → gates → finishing → fixings → job work →
 * despatch → every gate pass. Uses lucide-react icons; kebab keys map here.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ArrowDownCircle, ShieldCheck, Sparkles, Wrench, ArrowRightCircle,
  ArrowLeftCircle, Truck, DoorOpen, Clock,
} from "lucide-react";

const iconMap = {
  "arrow-down": ArrowDownCircle,
  "shield-check": ShieldCheck,
  "sparkles": Sparkles,
  "wrench": Wrench,
  "arrow-right": ArrowRightCircle,
  "arrow-left": ArrowLeftCircle,
  "truck": Truck,
  "door-open": DoorOpen,
};

const kindStyle = {
  grn:         { ring: "ring-emerald-200", dot: "bg-emerald-500",   label: "GRN" },
  gate:        { ring: "ring-blue-200",    dot: "bg-blue-500",      label: "QC Gate" },
  finishing:   { ring: "ring-amber-200",   dot: "bg-amber-500",     label: "Finishing" },
  fixing:      { ring: "ring-red-200",     dot: "bg-red-500",       label: "Fixing" },
  jw_out:      { ring: "ring-purple-200",  dot: "bg-purple-500",    label: "Job Work Out" },
  jw_return:   { ring: "ring-purple-100",  dot: "bg-purple-300",    label: "Job Work Return" },
  despatch:    { ring: "ring-slate-300",   dot: "bg-slate-700",     label: "Despatch" },
  gate_pass:   { ring: "ring-slate-100",   dot: "bg-slate-300",     label: "Gate Pass" },
};

export default function PieceLifecycleCard({ pieceId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/factory/pieces/${pieceId}/lifecycle`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Failed to load lifecycle"));
  }, [pieceId]);

  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded-lg p-3 mt-4 text-sm text-red-700"
        data-testid="piece-lifecycle-error">
        {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white border border-[#1B3A5C]/10 rounded-lg p-4 mt-4 text-xs text-[#7C8E9E]"
        data-testid="piece-lifecycle-loading">
        Loading lifecycle…
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#1B3A5C]/10 rounded-lg p-4 mt-4"
      data-testid="piece-lifecycle">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold" style={{ color: "var(--brand-navy)" }}>
          Piece lifecycle
        </h2>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#7C8E9E]">
          <Clock size={11} />
          {data.events.length} event{data.events.length === 1 ? "" : "s"}
        </div>
      </div>

      {data.at_job_worker && (
        <div className="mb-3 p-2 rounded bg-purple-50 border border-purple-200 text-xs text-purple-900"
          data-testid="piece-lifecycle-at-jw">
          Currently at Job Worker: <b>{data.at_job_worker_name}</b>
        </div>
      )}

      {data.events.length === 0 ? (
        <div className="text-xs text-[#7C8E9E] italic">
          No events yet — piece has just been created.
        </div>
      ) : (
        <div className="relative pl-6" data-testid="piece-lifecycle-events">
          {/* vertical rail */}
          <div className="absolute left-2 top-2 bottom-2 w-px bg-slate-200"></div>

          {data.events.map((e, i) => {
            const Icon = iconMap[e.icon] || ShieldCheck;
            const style = kindStyle[e.kind] || { ring: "ring-slate-100", dot: "bg-slate-400", label: e.kind };
            return (
              <div key={i} className="relative pb-4 last:pb-0"
                data-testid={`piece-event-${i}`}>
                {/* dot */}
                <div className={`absolute -left-[18px] top-1 w-4 h-4 rounded-full ring-4 ${style.ring} ${style.dot}`}></div>
                <div className="flex items-start gap-2">
                  <Icon size={16} className="text-[#4A627A] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <div className="text-sm font-medium text-[#0B2340]">{e.title}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">
                        {e.at ? new Date(e.at).toLocaleString(undefined, {
                          month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                        }) : "—"}
                      </div>
                    </div>
                    {e.detail && (
                      <div className="text-xs text-[#4A627A] mt-0.5 truncate">{e.detail}</div>
                    )}
                    <div className="mt-0.5">
                      <span className="text-[9px] uppercase tracking-widest text-[#7C8E9E]">
                        {style.label}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
